import { Badge, Box } from '@chakra-ui/react';
import { useMemo } from 'react';
import * as math from 'mathjs';
import { LineSegment2, Point2 } from '../../../domain/geometry/geometric-types';
import { getUnitScaleFromCalibration } from '../../../domain/sheet-calibration';
import { useSheetViewer } from '../../common/SheetViewer';
import { calculateSegmentLength } from '../../../domain/geometry/algorithms/util/line-segment';
import ProtanDiamond from './ProtanDiamond';
import { Diamond } from './SheetWedgeShapeDrawing';
import { onlyOneSide } from './kile-selector';

type DiamondLabelProps = {
  segment: LineSegment2;
  getPointInDomCoordinateSystem: (pointInPdf: Point2) => Point2;
  diamond: Diamond;
};

enum DiamondPlacement {
  ONESIDE = 'ONESIDE',
  CENTER = 'CENTER',
}

export function createDiamondAroundSegment(
  segment: LineSegment2,
  diamondPlacement: DiamondPlacement
): Point2[][] {
  const [start, end] = segment;

  // Calculate the midpoint of the line segment
  const midPoint: Point2 = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];

  // Calculate the distance between start and end points (length of the segment)
  const length = Math.sqrt((end[0] - start[0]) ** 2 + (end[1] - start[1]) ** 2);

  // Calculate the angle of the segment
  const angle = Math.atan2(end[1] - start[1], end[0] - start[0]);

  const widthLengthFactorSingle = 3600 / 14400;
  const widthLengthFactorDouble = 3600 / (14400 * 2);

  if (diamondPlacement === DiamondPlacement.ONESIDE) {
    const trianglePoints: Point2[] = [
      [end[0], end[1]],
      [start[0], start[1]],
      [
        end[0] +
          length * widthLengthFactorSingle * Math.cos(angle + Math.PI / 2),
        end[1] +
          length * widthLengthFactorSingle * Math.sin(angle + Math.PI / 2),
      ],
    ];
    const trianglePoints2: Point2[] = [
      [start[0], start[1]],
      [end[0], end[1]],
      [
        start[0] +
          length * widthLengthFactorSingle * Math.cos(angle + Math.PI / 2),
        start[1] +
          length * widthLengthFactorSingle * Math.sin(angle + Math.PI / 2),
      ],
    ];

    const trianglePoints3: Point2[] = [
      [end[0], end[1]],
      [start[0], start[1]],
      [
        end[0] +
          length *
            widthLengthFactorSingle *
            Math.cos(angle + (Math.PI * 2 * 3) / 4),
        end[1] +
          length *
            widthLengthFactorSingle *
            Math.sin(angle + (Math.PI * 2 * 3) / 4),
      ],
    ];
    const trianglePoints4: Point2[] = [
      [start[0], start[1]],
      [end[0], end[1]],
      [
        start[0] +
          length *
            widthLengthFactorSingle *
            Math.cos(angle + (Math.PI * 2 * 3) / 4),
        start[1] +
          length *
            widthLengthFactorSingle *
            Math.sin(angle + (Math.PI * 2 * 3) / 4),
      ],
    ];

    return [trianglePoints, trianglePoints2, trianglePoints3, trianglePoints4];
  }
  // Calculate the coordinates of the diamond points
  const trianglePoints: Point2[] = [
    [midPoint[0], midPoint[1]],
    [start[0], start[1]],
    [
      midPoint[0] +
        length * widthLengthFactorDouble * Math.cos(angle + Math.PI / 2),
      midPoint[1] +
        length * widthLengthFactorDouble * Math.sin(angle + Math.PI / 2),
    ],
  ];
  const trianglePoints2: Point2[] = [
    [midPoint[0], midPoint[1]],
    [end[0], end[1]],
    [
      midPoint[0] +
        length * widthLengthFactorDouble * Math.cos(angle + Math.PI / 2),
      midPoint[1] +
        length * widthLengthFactorDouble * Math.sin(angle + Math.PI / 2),
    ],
  ];

  const trianglePoints3: Point2[] = [
    [midPoint[0], midPoint[1]],
    [start[0], start[1]],
    [
      midPoint[0] +
        length *
          widthLengthFactorDouble *
          Math.cos(angle + (Math.PI * 2 * 3) / 4),
      midPoint[1] +
        length *
          widthLengthFactorDouble *
          Math.sin(angle + (Math.PI * 2 * 3) / 4),
    ],
  ];
  const trianglePoints4: Point2[] = [
    [midPoint[0], midPoint[1]],
    [end[0], end[1]],
    [
      midPoint[0] +
        length *
          widthLengthFactorDouble *
          Math.cos(angle + (Math.PI * 2 * 3) / 4),
      midPoint[1] +
        length *
          widthLengthFactorDouble *
          Math.sin(angle + (Math.PI * 2 * 3) / 4),
    ],
  ];

  return [trianglePoints, trianglePoints2, trianglePoints3, trianglePoints4];
}
export function DiamondLabel({
  segment,
  getPointInDomCoordinateSystem,
  diamond,
}: DiamondLabelProps) {
  const segmentInDom = useMemo<LineSegment2>(() => {
    const p1 = getPointInDomCoordinateSystem(segment[0]);
    const p2 = getPointInDomCoordinateSystem(segment[1]);
    return [p1, p2];
  }, [getPointInDomCoordinateSystem, segment]);

  const {
    calibration: { calibration },
  } = useSheetViewer();
  const unitScale = useMemo(
    () =>
      calibration ? getUnitScaleFromCalibration(calibration.calibration) : null,
    [calibration]
  );

  if (!unitScale) {
    return null;
  }

  const onlyOneSideVar = onlyOneSide(diamond);
  const trianglePoints = createDiamondAroundSegment(
    segment,
    onlyOneSideVar ? DiamondPlacement.ONESIDE : DiamondPlacement.CENTER
  );

  if (!diamond) return <></>;
  return (
    <>
      <Box
        position="absolute"
        zIndex={2}
        style={{
          top:
            segmentInDom[0][1] -
            0.5 * (segmentInDom[0][1] - segmentInDom[1][1]),
          left:
            segmentInDom[0][0] -
            0.5 * (segmentInDom[0][0] - segmentInDom[1][0]),
        }}
        pointerEvents="none"
      >
        <Badge
          textTransform={'none'}
          size={'md'}
          px={2}
          variant={'subtle'}
          colorScheme={'gray'}
        >
          {math.round(calculateSegmentLength(segment) * unitScale, 2)} m
        </Badge>
      </Box>
      <ProtanDiamond
        diamond={diamond}
        trianglePoints={trianglePoints}
        getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
      ></ProtanDiamond>
    </>
  );
}
